'use client'

import React, { ReactNode } from 'react'
import { Modal, Stack } from '@mantine/core'
import { useBreakpoint } from '@/hooks/useBreakpoints'

interface BaseModalProps {
    opened: boolean
    onClose: () => void
    children: ReactNode
    zIndex?: number
    maxWidth?: string | number
    padding?: string
}

export default function BaseModal({ opened, onClose, children, zIndex = 1000, maxWidth, padding }: BaseModalProps) {
    const { isSm } = useBreakpoint()

    const defaultMaxWidth = isSm ? '90vw' : '1000px'
    const defaultPadding = isSm ? '32px 24px' : '10px 40px 48px 40px'

    const finalMaxWidth = maxWidth || defaultMaxWidth
    const maxWidthValue = typeof finalMaxWidth === 'number' ? `${finalMaxWidth}px` : finalMaxWidth

    return (
        <Modal
            opened={opened}
            onClose={onClose}
            centered
            withCloseButton={true}
            closeButtonProps={{
                'aria-label': 'Close modal',
            }}
            size="auto"
            zIndex={zIndex}
            styles={{
                body: {
                    padding: padding || defaultPadding,
                },
                content: {
                    borderRadius: '24px',
                    maxWidth: maxWidthValue,
                    width: '100%',
                    backgroundColor: '#ffffff',
                    margin: '0 auto',
                },
                header: {
                    padding: 0,
                    marginBottom: 0,
                    backgroundColor: '#ffffff',
                },
                close: {
                    border: 'none',
                    outline: 'none',
                    boxShadow: 'none',
                    right: '24px',
                    top: '12px',
                    width: '52px',
                    height: '52px',
                    minWidth: '52px',
                    minHeight: '52px',
                    borderRadius: '50%',
                    background: 'var(--Background-bg-tonal, rgba(43, 42, 42, 0.05))',
                    color: '#1f1e1d',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    '& svg': {
                        width: '32px',
                        height: '32px',
                    },
                    '&:hover': {
                        background: 'var(--Background-bg-tonal, rgba(43, 42, 42, 0.05))',
                        border: 'none',
                        outline: 'none',
                        boxShadow: 'none',
                    },
                    '&:focus, &:focus-visible': {
                        border: 'none',
                        outline: 'none',
                        boxShadow: 'none',
                    },
                },
                overlay: {
                    zIndex: zIndex,
                },
                inner: {
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    padding: '16px',
                },
            }}
        >
            <Stack w="100%" maw={730} mx="auto">
                {children}
            </Stack>
        </Modal>
    )
}
